import React, { useEffect, useState } from 'react';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { ButcherHeader } from '@components/ButcherHeader';
import {
  errorMessage,
  ManagementSubsteps,
  onboardingStepId,
  responseTexts,
} from '@helpers/Constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faSearch,
  faPen,
} from '@fortawesome/free-solid-svg-icons';
import AddUserModal from '@components/Modal/UserManagemet/AddUser';
import { useToast } from '@contexts/Toast';
import {
  ApiEndpoints,
  FrontendRoutes,
  onboardingSteps,
} from '@helpers/Constants/Navigation';
import { useLocation, useNavigate } from 'react-router-dom';
import { Axios } from '@helpers/ApiService';
import { RoleUser } from '@helpers/Models';
import EditUserModal from '@components/Modal/UserManagemet/EditUser';
import { OnBoardSettings } from '@components/OnBoardSettings';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';

export const UserManagement: React.FC = () => {
  const { updateOnboardingStep } = useLocalStorage();
  const [isAddingNewUser, setIsAddingNewUser] = useState(false);
  const [users, setUsers] = useState<RoleUser[]>([]);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { showToastMessage } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const fromSettings = location.state?.fromSettings;

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    const response = await Axios.post(ApiEndpoints.FETCH_USER_ROLES, {});
    setUsers(response.data.data);
  };

  const editUser = (id: number): void => {
    setSelectedUserId(id);
    setIsEditingUser(true);
  };

  const deleteUser = async (id: number) => {
    try {
      await Axios.delete(ApiEndpoints.DELETE_USER_ROLE, { data: { id } });
      fetchRoles();
    } catch (error) {
      showToastMessage(errorMessage.ERROR_DELETING_USER, 'error');
    }
  };

  const handleSubmit: (
    event: React.FormEvent,
    nextPage: boolean,
  ) => Promise<void> = async (event, nextPage) => {
    event.preventDefault();

    await Axios.post(ApiEndpoints.UPDATE_ONBOARDING_STEP, {
      stepName: onboardingSteps.USER_MANAGEMENT,
    });

    updateOnboardingStep(onboardingStepId.USER_ROLE_MANAGEMENT);
    if (nextPage) {
      showToastMessage(responseTexts.USER_UPSERT_SUCCESS, 'success');
      if (fromSettings) {
        navigate(FrontendRoutes.OPERATION_MANAGEMENT, {
          state: { fromSettings },
        });
      } else {
        navigate(FrontendRoutes.OPERATION_MANAGEMENT);
      }
      return;
    } else {
      showToastMessage(responseTexts.USER_UPSERT_SUCCESS, 'success');
    }
  };

  const filteredUsers = users.filter((user: RoleUser) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;

    const matchesName = user.name.toLowerCase().includes(query);
    const matchesEmail = user.email.toLowerCase().includes(query);
    const matchesRole = user.roles.some((role) =>
      role.name.toLowerCase().includes(query),
    );

    return matchesName || matchesEmail || matchesRole;
  });

  const renderContent = (
    <>
      {/* Breadcrumbs */}
      <div>
        <Breadcrumbs
          title="Configure Settings"
          activeMainNav="Roles and users"
          activeStep={2}
          subNavSteps={ManagementSubsteps}
          fromSettings={fromSettings ? true : false}
          forStoreSetting={fromSettings ? true : false}
        />
      </div>
      <hr className="my-4 border-t border-gray-300" />

      {/* Content Section */}
      <div id="search-header" className="pb-20">
        {/* Header with title, +Add User, and search bar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-xl font-bold">User Management</h2>

          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto justify-end">
            {/* + Add User button */}
            <button
              onClick={() => setIsAddingNewUser(true)}
              className="h-10 px-6 text-[13px] font-semibold border border-primary rounded-md text-white bg-primary hover:bg-dark-primary transition-colors duration-200 whitespace-nowrap"
              type="button"
            >
              + Add User
            </button>

            {/* Search bar */}
            <div className="flex w-72 sm:w-80 px-4 py-1 items-center rounded border border-[#C9C5C6] bg-white">
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pr-4 py-1 outline-none border-none focus:ring-0 text-sm"
              />
              <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 mt-4 overflow-y-auto mb-6 relative shadow-lg h-[calc(100vh-450px)]">
          <table className="w-full bg-white border border-gray-200 shadow-md rounded-md">
            <thead className="bg-gray-200 sticky top-0 z-10">
              <tr>
                <th className="py-2 px-4 text-left">Username</th>
                <th className="py-2 px-4 text-left">Email</th>
                <th className="py-2 px-4 text-left">Role</th>
                <th className="py-2 px-4 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-4 text-center text-gray-500">
                    No users found.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user: RoleUser, index: number) => (
                  <tr
                    key={index}
                    className="border-b hover:bg-gray-50 transition duration-150 items-center"
                  >
                    <td className="py-2 px-4 block">
                      <div className="flex items-center gap-2">
                        <span
                          className={`h-2 w-2 rounded-full ${user.activationStatus === 'Active' ? 'bg-green-500' : 'bg-gray-400'}`}
                        ></span>
                        <span className="text-sm">{user.activationStatus}</span>
                      </div>
                      <span className="block font-medium mt-1 pl-4">
                        {user.name}
                      </span>
                    </td>

                    <td className="py-2 px-4">{user.email}</td>
                    <td className="py-2 px-4">
                      {user.roles.map((role, i) => (
                        <div key={i}>{role.name}</div>
                      ))}
                    </td>
                    <td className="py-2 px-4">
                      <div className="gap-3 items-center flex">
                        {user.isEditable == 1 && (
                          <>
                            <button
                              onClick={() => editUser(user.id)}
                              type="button"
                              className="text-[#000A16B2]"
                            >
                              <FontAwesomeIcon icon={faPen} />
                            </button>
                            <button
                              onClick={() => deleteUser(user.id)}
                              type="button"
                              className="text-[#4D545C]"
                            >
                              <FontAwesomeIcon icon={faTrashCan} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Modals */}
        <div className="modal-divs">
          <AddUserModal
            isOpen={isAddingNewUser}
            onClose={() => {
              setIsAddingNewUser(false);
              fetchRoles();
            }}
          />
          {selectedUserId &&
            (() => {
              const selectedUser = users.find(
                (user) => user.id === selectedUserId,
              );
              return selectedUser ? (
                <EditUserModal
                  isOpen={isEditingUser}
                  onClose={() => {
                    setIsEditingUser(false);
                    fetchRoles();
                  }}
                  user={selectedUser}
                />
              ) : null;
            })()}
        </div>
      </div>

      {/* Sticky Footer – only Next button */}
      <div className="sticky bottom-0 bg-white mt-3 border-t border-gray-100">
        <div className="w-full py-6">
          <div className="flex flex-col 2xl:flex-row 2xl:items-center 2xl:justify-end gap-4 w-full">
            <button
              type="button"
              onClick={(e) => handleSubmit(e, true)}
              className="w-full 2xl:w-[150px] h-10 px-6 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2 transition-colors duration-200"
            >
              <span>Next</span>
              <FontAwesomeIcon icon={faArrowRight} className="ml-1" />
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      {!fromSettings ? (
        <div className="w-screen h-screen overflow-auto flex justify-center items-start">
          <div className="w-11/12 p-4">
            <ButcherHeader isOnBoarding={true} />
            {renderContent}
          </div>
        </div>
      ) : (
        <OnBoardSettings renderContent={renderContent} />
      )}
    </>
  );
};